/**
 * theme/PreferencesProvider.tsx —— 设置面 **注入位**（S1：面板／命令／呈现读同一份事实）。
 *
 * 纪律：Provider 只做「读快照 → 应用呈现 → 供上下文」；写入一律经 `PreferencesStore`（带 revision 围栏）。
 */
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { applyPreferences, type AppliedPresentation } from "./apply";
import {
  PreferencesStore, type Preferences, type PreferencesSnapshot,
} from "./preferences";
import { E_CODES } from "../transport/errorText";

export interface PreferencesContextValue {
  store: PreferencesStore;
  snapshot: PreferencesSnapshot;
  applied: AppliedPresentation;
  /** 写入口（面板与命令**共用**此路径；无第二写路径） */
  update: (patch: Partial<Preferences>) => void;
  setSessionView: (sessionId: string, viewName: string) => void;
}

const PreferencesContext = createContext<PreferencesContextValue | null>(null);

export interface PreferencesProviderProps {
  store?: PreferencesStore;
  children: React.ReactNode;
}

export function PreferencesProvider({ store, children }: PreferencesProviderProps): React.ReactElement {
  const [active] = useState<PreferencesStore>(() => store ?? new PreferencesStore());
  const [snapshot, setSnapshot] = useState<PreferencesSnapshot>(() => active.snapshot());

  useEffect(() => active.subscribe(setSnapshot), [active]);

  const applied = useMemo(
    () => applyPreferences(snapshot.values, typeof document === "undefined" ? null : document.documentElement),
    [snapshot],
  );

  const value = useMemo<PreferencesContextValue>(() => ({
    store: active,
    snapshot,
    applied,
    update: (patch) => { active.update(patch); },
    setSessionView: (sessionId, viewName) => { active.setSessionView(sessionId, viewName); },
  }), [active, snapshot, applied]);

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
}

/** 取设置面；未包 Provider ⇒ 抛（不静默造默认，不判绿） */
export function usePreferences(): PreferencesContextValue {
  const value = useContext(PreferencesContext);
  if (!value) throw new Error(E_CODES.PREFERENCES_CONTEXT + "：缺少 PreferencesProvider");
  return value;
}
